import { computed, Injectable, PLATFORM_ID, signal, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TUI_DARK_MODE } from '@taiga-ui/core';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'metasmile.theme';

/**
 * The app's light/dark preference — and the single source of truth for Taiga's
 * too.
 *
 * WHY IT DRIVES TUI_DARK_MODE. Taiga runs its own dark-mode system: an app
 * initializer in @taiga-ui/core does
 *
 *     if (mode()) doc.body.setAttribute('tuiTheme', 'dark')
 *     else        doc.body.removeAttribute('tuiTheme')
 *
 * where `mode()` reads localStorage['tuiDark'] and otherwise follows
 * `prefers-color-scheme`, LIVE — it subscribes to the media query's change
 * event for as long as that key is unset.
 *
 * This app never wrote that key, so Taiga stayed in automatic mode and stamped
 * `tuiTheme="dark"` on <body> whenever the OS went dark, regardless of what the
 * user had chosen in-app. That alone would be survivable, because the app sets
 * its own `tuiTheme` on <tui-root> further down the tree — except for how the
 * palette in styles.css is wired:
 *
 *     :root                      --bg-main: #ffffff
 *     [tuiTheme='dark']          --bg-main: #161a21          <- lands on BODY
 *     :root,[tuiTheme='light']   --tui-background-base: var(--bg-main)
 *
 * The light block derives the Taiga surface FROM --bg-main, and --bg-main is
 * inherited from <body>, where Taiga's dark attribute had just redefined it.
 * So "light" resolved to the dark surface, and the whole app went dark while
 * the toggle still said light.
 *
 * Setting TUI_DARK_MODE here keeps the two attributes in agreement and, as a
 * side effect, writes the `tuiDark` key — which is exactly what unsubscribes
 * Taiga from the media query. The OS preference still seeds the FIRST visit
 * (see restore()); it just stops overriding a choice the user has made.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly tuiDarkMode = inject(TUI_DARK_MODE);
  private readonly theme = signal<Theme>(this.restore());

  readonly current = this.theme.asReadonly();
  readonly isDark = computed(() => this.theme() === 'dark');

  constructor() {
    // Adopt whatever restore() decided, including the first-visit OS default,
    // so <body> and <tui-root> never disagree even before the first toggle.
    this.syncTaiga(this.theme());
  }

  toggle(): void {
    this.set(this.theme() === 'dark' ? 'light' : 'dark');
  }

  set(theme: Theme): void {
    this.theme.set(theme);
    this.syncTaiga(theme);

    if (this.isBrowser) {
      localStorage.setItem(STORAGE_KEY, theme);
    }
  }

  private syncTaiga(theme: Theme): void {
    // Guarded: TUI_DARK_MODE's factory touches window.matchMedia, so it is
    // browser-only. On the server the attribute is irrelevant anyway.
    if (this.isBrowser) {
      this.tuiDarkMode.set(theme === 'dark');
    }
  }

  private restore(): Theme {
    if (!this.isBrowser) {
      return 'light';
    }

    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored === 'light' || stored === 'dark') {
      return stored;
    }

    // First visit only. Once the user chooses, set() persists it and the OS no
    // longer gets a say.
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
}
